/**
 * ProviderMonogram — Distinctive branded monogram for cloud providers.
 *
 * Each provider gets a unique visual treatment:
 * - Azure: frosted glass pill with "Az" and subtle inner glow
 * - AWS: warm gradient with "aws" and orbital ring accent
 * - GCP: 4-color dot constellation with gentle float animation
 * - Fallback: letter on muted surface
 */

interface ProviderMonogramProps {
  providerCode: string;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
}

const SIZE = {
  sm: { box: 'h-8 w-8', text: 'text-[12px]', awsText: 'text-[10px]', dot: 'h-[5px] w-[5px]', gap: 'gap-[3px]' },
  md: { box: 'h-9 w-9', text: 'text-[13px]', awsText: 'text-[11px]', dot: 'h-[6px] w-[6px]', gap: 'gap-[3px]' },
  lg: { box: 'h-11 w-11', text: 'text-[15px]', awsText: 'text-[13px]', dot: 'h-[7px] w-[7px]', gap: 'gap-1' },
} as const;

const GCP_COLORS = ['#4285F4', '#EA4335', '#FBBC04', '#34A853'] as const;

export default function ProviderMonogram({ providerCode, size = 'sm', animated = true }: ProviderMonogramProps) {
  const s = SIZE[size];
  const code = providerCode.toLowerCase();

  if (code === 'azure') {
    return (
      <div
        className={`${s.box} relative flex items-center justify-center rounded-[10px] flex-shrink-0 select-none overflow-hidden`}
        style={{ background: 'linear-gradient(145deg, #0078D4 0%, #00BCF2 50%, #50E6FF 100%)' }}
        aria-hidden="true"
      >
        {/* Inner glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
        <span className={`${s.text} relative font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.15)]`} style={{ letterSpacing: '-0.03em' }}>
          Az
        </span>
      </div>
    );
  }

  if (code === 'aws') {
    return (
      <div
        className={`${s.box} relative flex items-center justify-center rounded-[10px] flex-shrink-0 select-none overflow-hidden`}
        style={{ background: 'linear-gradient(145deg, #FF9900 0%, #FF6600 60%, #E85D04 100%)' }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent" />
        {/* Subtle orbital arc */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(ellipse 120% 40% at 50% 80%, white, transparent)',
          }}
        />
        <span className={`${s.awsText} relative font-extrabold uppercase text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.15)]`} style={{ letterSpacing: '0.06em' }}>
          aws
        </span>
      </div>
    );
  }

  if (code === 'gcp') {
    return (
      <div
        className={`${s.box} flex items-center justify-center rounded-[10px] bg-gray-50 dark:bg-neutral-800/80 flex-shrink-0 select-none border border-gray-200/60 dark:border-neutral-700/40`}
        aria-hidden="true"
      >
        <div className={`grid grid-cols-2 ${s.gap}`}>
          {GCP_COLORS.map((color, i) => (
            <span
              key={i}
              className={`${s.dot} rounded-full ${animated ? 'transition-transform duration-500' : ''}`}
              style={{
                backgroundColor: color,
                boxShadow: `0 0 4px ${color}66`,
                animationDelay: animated ? `${i * 75}ms` : undefined,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  /* Fallback */
  return (
    <div
      className={`${s.box} flex items-center justify-center rounded-[10px] bg-gray-300 dark:bg-gray-600 text-white text-xs font-bold flex-shrink-0 select-none`}
      aria-hidden="true"
    >
      {providerCode.charAt(0).toUpperCase()}
    </div>
  );
}
